import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/shared/utils/utils';

/**
 * A shape placeholder. It is decoration, not content, so it is hidden from the
 * accessibility tree — the region that owns it carries `aria-busy` and a label
 * (see LoadingRegion), which is how the loading state gets announced.
 */
export function Skeleton({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div aria-hidden className={cn('animate-pulse rounded-lg bg-surface-dim', className)} {...props} />;
}

/**
 * Marks a region as loading without changing what is inside it: `role="status"`
 * announces the state once, `aria-busy` keeps assistive tech out of the rows
 * that are not real yet, and the visually-hidden label says what is happening
 * (an icon-only skeleton says nothing to a screen reader).
 */
export function LoadingRegion({
  busy,
  label = 'Loading',
  className,
  children,
}: {
  busy: boolean;
  label?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn(className)} aria-busy={busy || undefined}>
      {busy && (
        <span role="status" className="sr-only">
          {label}
        </span>
      )}
      {children}
    </div>
  );
}
